import { useState, useRef, useEffect, useCallback } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Video from "../assets/images/video.jpeg";
import { SUPER_ADMIN, DASHBOARD_EDITOR } from "../constants";
import {
  updateScanStageDefect,
  updateScanStageImages,
} from "../pages/ReportDetails/api";
import { errorMessage, successMessage } from "./Toaster";

const DEFECT_COLORS = {
  "CORNER POST CUT": "#0d7be9",
  CRACK: "#00ffbc",
  DEFORMATION: "#2ae3eb",
  "DENT - MAJOR": "#ccc60f",
  "DENT - MINOR": "#ff0000",
  "DENT AND RUST": "#7c0303",
  "RUST - MAJOR": "#3acbc9",
  "RUST - MINOR": "#00ffbc",
  "RUST - UPPER": "#2ae3eb",
  "RUST - FRONT": "#3acbc9",
  "RUST - BACK DOOR": "#ec6219",
  RUST: "#2ae3eb",
  DENT: "#00ffbc",
  OTHER: "#0fec1b",
};
const colorFor = (name) => DEFECT_COLORS[name] ?? "#ef4444";
const hexToRgba = (hex, a = 0.15) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${a})`;
};

const parseImageMeta = (fileName) => {
  if (!fileName) return { defect_side: "UNKNOWN", image_no: 1 };
  const clean = fileName
    .replace(/_updated_\d{8}(?:_\d{6})?\./i, ".")
    .replace(/_updated\./i, ".");
  const m = clean.match(/_(.+)_(\d+)(?:_(external|internal))?\.\w+$/i);
  if (m) return { defect_side: m[1].toUpperCase(), image_no: parseInt(m[2], 10) };
  return { defect_side: "UNKNOWN", image_no: 1 };
};

const getImageLabel = (fileName) => {
  if (!fileName) return "";
  const lower = fileName.toLowerCase();
  if (lower.includes("tank_number")) return "Tank Number";
  if (lower.includes("container_number")) return "Container Number";
  if (lower.includes("iso_number")) return "ISO Number";
  if (lower.includes("seal_code")) return "Seal Code";
  if (lower.includes("plate")) return "Plate";
  if (lower.includes("vehicle_number_detected")) {
    const m = lower.match(/vehicle_number_detected_(\d+)(?:_(external|internal))?/);
    if (m) return `${m[1]}_detected${m[2] ? `_${m[2]}` : ""}`;
  }
  if (lower.includes("vehicle_number")) {
    const m = lower.match(/vehicle_number_(\d+)(?:_(external|internal))?/);
    if (m) return `${m[1]}${m[2] ? `_${m[2]}` : ""}`;
  }
  const clean = fileName
    .replace(/_updated_\d{8}(?:_\d{6})?\./i, ".")
    .replace(/_updated\./i, ".");
  const m = clean.match(/_(.+)_(\d+)(?:_(external|internal))?\.\w+$/i);
  if (m) {
    const side = m[1].toUpperCase(), num = m[2], pos = m[3]?.toLowerCase() || "";
    if (side === "UNKNOWN") return pos ? `${num}_${pos}` : `${num}`;
    return pos ? `${num}_${side}_${pos}` : `${num}_${side}`;
  }
  return fileName.replace(/\.\w+$/, "");
};

const canvasToFile = (canvas, fileName) =>
  new Promise((resolve) => {
    canvas.toBlob(
      (blob) => resolve(new File([blob], fileName, { type: "image/jpeg" })),
      "image/jpeg",
      0.92,
    );
  });

const drawLegend = (canvas, boxes) => {
  if (!canvas || boxes.length === 0) return;
  const ctx = canvas.getContext("2d");
  const W = canvas.width;
  const cols = 2;
  const fontSize = Math.max(12, Math.min(16, Math.round(W * 0.012)));
  const itemHeight = fontSize + 16;
  const rows = Math.ceil(boxes.length / cols);
  const legendH = rows * itemHeight + 16;
  const tempCanvas = document.createElement("canvas");
  tempCanvas.width = W;
  tempCanvas.height = canvas.height;
  tempCanvas.getContext("2d").drawImage(canvas, 0, 0);
  const imageH = canvas.height;
  canvas.height = imageH + legendH;
  ctx.drawImage(tempCanvas, 0, 0);
  ctx.fillStyle = "#000000";
  ctx.fillRect(0, imageH, W, legendH);
  const colW = W / cols;
  const dotSize = Math.max(10, Math.round(fontSize * 0.9));
  ctx.font = `bold ${fontSize}px sans-serif`;
  ctx.textBaseline = "middle";
  boxes.forEach((box, i) => {
    const col = i % cols;
    const row = Math.floor(i / cols);
    const x = col * colW + 16;
    const y = imageH + 8 + row * itemHeight + itemHeight / 2;
    ctx.fillStyle = box.color;
    ctx.fillRect(x, y - dotSize / 2, dotSize, dotSize);
    ctx.fillStyle = "#ffffff";
    ctx.fillText(`${box.defect_no}. ${box.label}`, x + dotSize + 8, y);
  });
  ctx.textBaseline = "alphabetic";
};

const drawCanvas = (canvas, imageObj, boxes, hoverId, defectName, livePx) => {
  if (!canvas || !imageObj) return;
  const W = imageObj.naturalWidth || imageObj.width || canvas.width;
  const H = imageObj.naturalHeight || imageObj.height || canvas.height;
  const ctx = canvas.getContext("2d");
  canvas.width = W;
  canvas.height = H;
  ctx.drawImage(imageObj, 0, 0, W, H);
  boxes.forEach((box) => {
    const { x, y, w, h } = box;
    const col = box.color;
    const isHover = box.id === hoverId;
    const label = `${box.defect_no ?? "?"}: ${box.label}`;
    const fontSize = Math.max(12, Math.min(22, Math.round(W * 0.009)));
    ctx.font = `bold ${fontSize}px sans-serif`;
    const textW = ctx.measureText(label).width;
    const padX = 10, padY = 5;
    const labelH = fontSize + padY * 2;
    const labelY = Math.max(0, y - labelH);
    ctx.fillStyle = "black";
    ctx.beginPath();
    if (ctx.roundRect) ctx.roundRect(x, labelY, textW + padX * 2, labelH, [4, 4, 0, 0]);
    else ctx.rect(x, labelY, textW + padX * 2, labelH);
    ctx.fill();
    ctx.fillStyle = "#fff";
    ctx.textBaseline = "middle";
    ctx.fillText(label, x + padX, labelY + labelH / 2);
    ctx.textBaseline = "alphabetic";
    ctx.strokeStyle = col;
    ctx.lineWidth = isHover ? 4 : 2;
    ctx.setLineDash([]);
    ctx.strokeRect(x, y, w, h);
    if (isHover) {
      ctx.fillStyle = hexToRgba(col, 0.12);
      ctx.fillRect(x, y, w, h);
    }
  });
  if (livePx) {
    const col = colorFor(defectName);
    ctx.strokeStyle = col;
    ctx.lineWidth = 4;
    ctx.setLineDash([]);
    ctx.strokeRect(livePx.x, livePx.y, livePx.w, livePx.h);
    ctx.fillStyle = hexToRgba(col, 0.15);
    ctx.fillRect(livePx.x, livePx.y, livePx.w, livePx.h);
  }
};

const Chip = ({ bg, color, children }) => (
  <span style={{ fontSize: 9, background: bg, color, padding: "1px 6px", borderRadius: 20, fontWeight: 700, whiteSpace: "nowrap" }}>
    {children}
  </span>
);

const TBtn = ({ children, active, green, onClick, disabled }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    style={{
      display: "flex", alignItems: "center", gap: 5, padding: "5px 12px", borderRadius: 7,
      border: `1.5px solid ${active ? "#3b82f6" : green ? (disabled ? "#e2e8f0" : "#16a34a") : "#e2e8f0"}`,
      background: active ? "#eff6ff" : green ? (disabled ? "#f8fafc" : "#f0fdf4") : "#fff",
      color: active ? "#2563eb" : green ? (disabled ? "#94a3b8" : "#16a34a") : disabled ? "#cbd5e1" : "#475569",
      fontSize: 12, fontWeight: 600, cursor: disabled ? "not-allowed" : "pointer",
    }}
  >
    {children}
  </button>
);

const SBtn = ({ bg, border, color, flex, onClick, children }) => (
  <button
    onClick={onClick}
    style={{
      background: bg, border: `1px solid ${border}`, color, cursor: "pointer",
      fontSize: 10, padding: "2px 8px", borderRadius: 5, fontWeight: 600,
      ...(flex ? { flex: 1 } : {}),
    }}
  >
    {children}
  </button>
);

// ─── FilterView ───────────────────────────────────────────────────────────────
const FilterView = ({ open, onClose, imageSrc, fileName, allDefects }) => {
  const canvasRef = useRef(null);
  const imgRef = useRef(null);
  const blobUrlRef = useRef(null);
  const imageMeta = parseImageMeta(fileName);

  const [status, setStatus] = useState("idle");
  const [selectedType, setSelectedType] = useState("ALL");

  const imageDefects = (allDefects || []).filter((d) => d.image_no === imageMeta.image_no);
  const defectTypes = ["ALL", ...new Set(imageDefects.map((d) => d.defect_name))];

  useEffect(() => {
    return () => { if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current); };
  }, []);

  useEffect(() => {
    if (!open || !imageSrc) return;
    setStatus("loading");
    setSelectedType("ALL");
    let cancelled = false;
    const run = async () => {
      let srcUrl = imageSrc;
      try {
        const res = await fetch(imageSrc, { mode: "cors" });
        const blob = await res.blob();
        srcUrl = URL.createObjectURL(blob);
        blobUrlRef.current = srcUrl;
      } catch (e) { console.warn("Blob fetch failed:", e); }
      if (cancelled) return;
      await new Promise((resolve) => {
        const imageObj = new Image();
        imageObj.onload = () => {
          if (cancelled) { resolve(); return; }
          imgRef.current = imageObj;
          setStatus("ready");
          resolve();
        };
        imageObj.onerror = () => { if (!cancelled) setStatus("error"); resolve(); };
        if (srcUrl !== imageSrc) imageObj.crossOrigin = "anonymous";
        imageObj.src = srcUrl;
      });
    };
    run();
    return () => { cancelled = true; };
  }, [open, imageSrc]);

  useEffect(() => {
    if (status !== "ready" || !imgRef.current || !canvasRef.current) return;
    const freshImageDefects = (allDefects || []).filter((d) => d.image_no === imageMeta.image_no);
    const filtered = selectedType === "ALL"
      ? freshImageDefects
      : freshImageDefects.filter((d) => d.defect_name === selectedType);

    const canvas = canvasRef.current;
    const imageObj = imgRef.current;
    const W = imageObj.naturalWidth || imageObj.width;
    const H = imageObj.naturalHeight || imageObj.height;
    const ctx = canvas.getContext("2d");
    canvas.width = W;
    canvas.height = H;
    ctx.drawImage(imageObj, 0, 0, W, H);

    filtered.forEach((d) => {
      const x1 = d.coordinates.x1, y1 = d.coordinates.y1;
      const x2 = d.coordinates.x2, y2 = d.coordinates.y2;
      const col = colorFor(d.defect_name);
      ctx.strokeStyle = col;
      ctx.lineWidth = 2;
      ctx.strokeRect(x1, y1, x2 - x1, y2 - y1);
      ctx.fillStyle = hexToRgba(col, 0.15);
      ctx.fillRect(x1, y1, x2 - x1, y2 - y1);
    });
  }, [status, selectedType, allDefects]);

  const handleClose = () => {
    setStatus("idle");
    imgRef.current = null;
    if (blobUrlRef.current) { URL.revokeObjectURL(blobUrlRef.current); blobUrlRef.current = null; }
    onClose();
  };

  const filteredCount = selectedType === "ALL"
    ? imageDefects.length
    : imageDefects.filter((d) => d.defect_name === selectedType).length;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth
      PaperProps={{ sx: { height: "95vh", maxHeight: "95vh", borderRadius: "12px", display: "flex", flexDirection: "column" } }}
    >
      <DialogTitle sx={{ padding: "10px 16px", borderBottom: "1px solid #f1f5f9", flexShrink: 0 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <span style={{ fontSize: 14, fontWeight: 700, color: "#1e293b" }}>🔍 Filter Defects — {getImageLabel(fileName)}</span>
            <select value={selectedType} onChange={(e) => setSelectedType(e.target.value)}
              style={{ border: "1.5px solid #e2e8f0", borderRadius: 7, padding: "4px 10px", fontSize: 12, fontWeight: 600, outline: "none", background: "#fff", color: "#1e293b", cursor: "pointer", minWidth: 180 }}
            >
              {defectTypes.map((t) => (<option key={t} value={t}>{t}</option>))}
            </select>
            <span style={{ fontSize: 11, background: "#eff6ff", color: "#2563eb", padding: "2px 10px", borderRadius: 20, fontWeight: 700 }}>
              {filteredCount} defect{filteredCount !== 1 ? "s" : ""}
            </span>
          </div>
          <IconButton onClick={handleClose} size="small"><CloseIcon fontSize="small" /></IconButton>
        </div>
        <p style={{ fontSize: 11, color: "#94a3b8", marginTop: 6 }}>
          {status === "loading" ? "Loading image…" : status === "error" ? "⚠ Failed to load image" : "Read-only view — select a defect type from dropdown to filter"}
        </p>
      </DialogTitle>
      <DialogContent sx={{ padding: "12px", display: "flex", justifyContent: "center", alignItems: "center", overflow: "auto", background: "#f8fafc" }}>
        {status === "loading" && (<div style={{ color: "#64748b", fontSize: 13, textAlign: "center" }}><div style={{ fontSize: 24, marginBottom: 8 }}>⏳</div>Loading image…</div>)}
        {status === "error" && (<div style={{ color: "#ef4444", fontSize: 13, textAlign: "center" }}><div style={{ fontSize: 24, marginBottom: 8 }}>⚠️</div>Failed to load image.</div>)}
        <canvas ref={canvasRef}
          style={{ maxWidth: "100%", maxHeight: "calc(95vh - 130px)", width: "auto", height: "auto", display: status === "ready" ? "block" : "none", borderRadius: 8, boxShadow: "0 2px 12px rgba(0,0,0,0.1)" }}
        />
      </DialogContent>
    </Dialog>
  );
};

// ─── CanvasEditor ─────────────────────────────────────────────────────────────
const CanvasEditor = ({ open, onClose, originalSrc, fileName, allDefects, pendingBoxes, pendingDeletedIds, onCommit }) => {
  const canvasRef = useRef(null);
  const imgRef = useRef(null);
  const blobUrlRef = useRef(null);
  const imageMeta = parseImageMeta(fileName);

  const [boxes, setBoxes] = useState([]);
  const [deletedIds, setDeletedIds] = useState([]);
  const [history, setHistory] = useState([]); // ── undo history ──
  const [drawMode, setDrawMode] = useState(false);
  const [drawing, setDrawing] = useState(false);
  const [startNat, setStartNat] = useState({ nx: 0, ny: 0 });
  const [livePx, setLivePx] = useState(null);
  const [defectName, setDefectName] = useState("");
  const [renamingId, setRenamingId] = useState(null);
  const [renameVal, setRenameVal] = useState("");
  const [hoverId, setHoverId] = useState(null);
  const [status, setStatus] = useState("idle");

  useEffect(() => {
    return () => { if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current); };
  }, []);

  const reset = () => {
    setBoxes([]); setDeletedIds([]); setHistory([]);
    setDrawMode(false); setLivePx(null); setDrawing(false);
    setDefectName(""); setRenamingId(null); setHoverId(null);
    setStatus("idle"); imgRef.current = null;
    if (blobUrlRef.current) { URL.revokeObjectURL(blobUrlRef.current); blobUrlRef.current = null; }
  };

  // ── Save state before any mutation ──
  const saveHistory = useCallback(() => {
    setHistory((prev) => [...prev, { boxes, deletedIds }]);
  }, [boxes, deletedIds]);

  useEffect(() => {
    if (!open || !originalSrc) return;
    reset();
    setStatus("loading");
    let cancelled = false;
    const run = async () => {
      let srcUrl = originalSrc;
      try {
        const res = await fetch(originalSrc, { mode: "cors" });
        const blob = await res.blob();
        srcUrl = URL.createObjectURL(blob);
        blobUrlRef.current = srcUrl;
      } catch (e) { console.warn("Blob fetch failed:", e); }
      if (cancelled) return;
      await new Promise((resolve) => {
        const imageObj = new Image();
        imageObj.onload = () => {
          if (cancelled) { resolve(); return; }
          imgRef.current = imageObj;
          let initialBoxes;
          if (pendingBoxes && pendingBoxes.length > 0) {
            initialBoxes = pendingBoxes;
          } else {
            const relevant = (allDefects || []).filter((d) => d.image_no === imageMeta.image_no);
            initialBoxes = relevant.map((d) => ({
              id: `existing_${d.defect_id}`, defect_id: d.defect_id, defect_no: d.defect_no,
              isExisting: true, label: d.defect_name, color: colorFor(d.defect_name),
              x: d.coordinates.x1, y: d.coordinates.y1,
              w: d.coordinates.x2 - d.coordinates.x1, h: d.coordinates.y2 - d.coordinates.y1,
              defect: d,
            }));
          }
          if (pendingDeletedIds && pendingDeletedIds.length > 0) setDeletedIds(pendingDeletedIds);
          const canvas = canvasRef.current;
          if (canvas) {
            drawCanvas(canvas, imageObj, initialBoxes, null, "", null);
            drawLegend(canvas, initialBoxes);
          }
          setBoxes(initialBoxes);
          setStatus("ready");
          resolve();
        };
        imageObj.onerror = () => { if (!cancelled) setStatus("error"); resolve(); };
        if (srcUrl !== originalSrc) imageObj.crossOrigin = "anonymous";
        imageObj.src = srcUrl;
      });
    };
    run();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, originalSrc]);

  useEffect(() => {
    if (status !== "ready" || !imgRef.current) return;
    drawCanvas(canvasRef.current, imgRef.current, boxes, hoverId, defectName, livePx);
    if (!livePx && !drawMode) drawLegend(canvasRef.current, boxes);
  }, [boxes, hoverId, defectName, livePx, status, drawMode]);

  const toNatural = useCallback((dispX, dispY) => {
    const c = canvasRef.current;
    if (!c) return { nx: 0, ny: 0 };
    const rect = c.getBoundingClientRect();
    const scaleX = c.width / rect.width;
    const scaleY = c.height / rect.height;
    return { nx: Math.round(dispX * scaleX), ny: Math.round(dispY * scaleY) };
  }, []);

  const getPos = (e) => {
    const c = canvasRef.current;
    const rect = c.getBoundingClientRect();
    const cx = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    const cy = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;
    const { nx, ny } = toNatural(cx, cy);
    return { cx, cy, nx, ny };
  };

  const hitBox = (nx, ny) => {
    for (let i = boxes.length - 1; i >= 0; i--) {
      const b = boxes[i];
      if (nx >= b.x && nx <= b.x + b.w && ny >= b.y && ny <= b.y + b.h) return b;
    }
    return null;
  };

  const onMouseMove = (e) => {
    if (status !== "ready") return;
    const { nx, ny } = getPos(e);
    if (drawing) {
      setLivePx({ x: Math.min(startNat.nx, nx), y: Math.min(startNat.ny, ny), w: Math.abs(nx - startNat.nx), h: Math.abs(ny - startNat.ny) });
      return;
    }
    const box = hitBox(nx, ny);
    setHoverId(box ? box.id : null);
    if (canvasRef.current) canvasRef.current.style.cursor = drawMode ? "crosshair" : box ? "pointer" : "default";
  };

  const onMouseDown = (e) => {
    if (status !== "ready" || !drawMode) return;
    e.preventDefault();
    const { nx, ny } = getPos(e);
    setDrawing(true);
    setStartNat({ nx, ny });
    setLivePx({ x: nx, y: ny, w: 0, h: 0 });
  };

  const onMouseUp = (e) => {
    if (!drawing) return;
    setDrawing(false);
    const { nx, ny } = getPos(e);
    const x1 = Math.min(startNat.nx, nx), y1 = Math.min(startNat.ny, ny);
    const x2 = Math.max(startNat.nx, nx), y2 = Math.max(startNat.ny, ny);
    if (x2 - x1 < 5 || y2 - y1 < 5) { setLivePx(null); return; }
    saveHistory(); // ── save before draw ──
    const name = defectName || "DEFECT";
    setBoxes((prev) => {
      const allNos = [...(allDefects || []).map((d) => d.defect_no ?? 0), ...prev.map((b) => b.defect_no ?? 0)];
      const nextNo = allNos.length > 0 ? Math.max(...allNos) + 1 : 1;
      return [...prev, {
        id: `new_${Date.now()}`, defect_id: null, defect_no: nextNo, isExisting: false,
        label: name, color: colorFor(name), x: x1, y: y1, w: x2 - x1, h: y2 - y1,
        defect: {
          coordinates: { x1, y1, x2, y2 }, defect_code: null,
          defect_dimension: {
            area_cm2: parseFloat(((x2 - x1) * (y2 - y1)).toFixed(2)),
            length_cm: parseFloat((x2 - x1).toFixed(2)),
            width_cm: parseFloat((y2 - y1).toFixed(2)),
          },
          defect_name: name, defect_side: imageMeta.defect_side, image_no: imageMeta.image_no,
        },
      }];
    });
    setLivePx(null);
  };

  const removeBox = (id) => {
    saveHistory(); // ── save before remove ──
    const box = boxes.find((b) => b.id === id);
    if (box?.isExisting && box.defect_id) setDeletedIds((prev) => [...prev, box.defect_id]);
    setBoxes((prev) => prev.filter((b) => b.id !== id));
    setHoverId(null);
  };

  const commitRename = () => {
    if (!renamingId) return;
    saveHistory(); // ── save before rename ──
    const name = renameVal.trim() || "DEFECT";
    setBoxes((prev) => prev.map((b) => b.id === renamingId
      ? { ...b, label: name, color: colorFor(name), defect: { ...b.defect, defect_name: name } }
      : b
    ));
    setRenamingId(null);
  };

  // ── Undo last action (draw / remove / rename) ──
  const handleUndo = () => {
    if (history.length === 0) return;
    const prev = history[history.length - 1];
    setHistory((h) => h.slice(0, -1));
    setBoxes(prev.boxes);
    setDeletedIds(prev.deletedIds);
  };

  const handleApply = () => { onCommit(boxes, deletedIds, canvasRef.current); reset(); onClose(); };
  const handleDiscard = () => { reset(); onClose(); };

  const newCount = boxes.filter((b) => !b.isExisting).length;
  const delCount = deletedIds.length;
  const renamedCount = boxes.filter((b) => b.isExisting && b.label !== (allDefects || []).find((d) => d.defect_id === b.defect_id)?.defect_name).length;
  const hasChanges = newCount > 0 || delCount > 0 || renamedCount > 0;
  const drawBoxDisabled = !defectName;

  useEffect(() => { if (!defectName && drawMode) setDrawMode(false); }, [defectName, drawMode]);

  return (
    <Dialog open={open} onClose={handleDiscard} maxWidth="xl" fullWidth
      PaperProps={{ sx: { height: "97vh", maxHeight: "97vh", borderRadius: "12px", display: "flex", flexDirection: "column" } }}
    >
      <DialogTitle sx={{ padding: "10px 16px", borderBottom: "1px solid #f1f5f9", flexShrink: 0 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <span style={{ fontSize: 14, fontWeight: 700, color: "#1e293b" }}>✏ Edit Defects — {getImageLabel(fileName)}</span>
            {newCount > 0 && <Chip bg="#dcfce7" color="#16a34a">+{newCount} new</Chip>}
            {delCount > 0 && <Chip bg="#fee2e2" color="#dc2626">-{delCount} removed</Chip>}
            {renamedCount > 0 && <Chip bg="#fef9c3" color="#ca8a04">{renamedCount} renamed</Chip>}
          </div>
          <IconButton onClick={handleDiscard} size="small"><CloseIcon fontSize="small" /></IconButton>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", padding: "7px 12px", background: "#f8fafc", borderRadius: 10, border: "1px solid #e2e8f0" }}>
          <select value={defectName} onChange={(e) => setDefectName(e.target.value)}
            style={{ border: "1.5px solid #e2e8f0", borderRadius: 7, padding: "4px 10px", fontSize: 12, fontWeight: 500, width: 200, outline: "none", background: "#fff", color: defectName ? "#1e293b" : "#94a3b8", cursor: "pointer" }}
          >
            <option value="" disabled>Select defect type…</option>
            {Object.keys(DEFECT_COLORS).map((n) => (<option key={n} value={n}>{n}</option>))}
          </select>
          {defectName && (<div style={{ width: 20, height: 20, borderRadius: 4, background: colorFor(defectName), border: "2px solid #e2e8f0", flexShrink: 0 }} />)}
          <div style={{ width: 1, height: 26, background: "#e2e8f0", flexShrink: 0 }} />
          <TBtn active={drawMode} onClick={() => !drawBoxDisabled && setDrawMode((v) => !v)} disabled={drawBoxDisabled}>
            {drawMode ? "Drawing ON" : "Draw box"}
          </TBtn>
          <TBtn onClick={handleUndo} disabled={history.length === 0}>↩ Undo</TBtn>
          <div style={{ width: 1, height: 26, background: "#e2e8f0", flexShrink: 0 }} />
          <TBtn green onClick={handleApply} disabled={!hasChanges}>✓ Apply to Batch</TBtn>
          <button onClick={handleDiscard} style={{ padding: "5px 12px", borderRadius: 7, border: "1.5px solid #e2e8f0", background: "#fff", color: "#64748b", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
            Discard
          </button>
        </div>
        <p style={{ fontSize: 11, color: "#94a3b8", marginTop: 5, marginLeft: 2 }}>
          {status === "loading" ? "Loading image…" : status === "error" ? "⚠ Failed to load image — check CORS settings"
            : drawBoxDisabled ? "Select a defect type from the dropdown to enable drawing"
            : drawMode ? "Click and drag on the image to draw a new defect box"
            : 'Changes are staged locally — use "Save All Changes" in the viewer to send to server'}
        </p>
      </DialogTitle>
      <DialogContent sx={{ padding: 0, display: "flex", flex: 1, overflow: "hidden" }}>
        <div style={{ flex: "1 1 0", overflow: "auto", background: "#f8fafc", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
          {status === "loading" && (<div style={{ color: "#64748b", fontSize: 13, textAlign: "center" }}><div style={{ fontSize: 24, marginBottom: 8 }}>⏳</div>Loading image…</div>)}
          {status === "error" && (<div style={{ color: "#ef4444", fontSize: 13, textAlign: "center" }}><div style={{ fontSize: 24, marginBottom: 8 }}>⚠️</div>Failed to load image.<br />Check CORS settings in Azure Storage.</div>)}
          <canvas
            ref={canvasRef}
            onMouseMove={onMouseMove} onMouseDown={onMouseDown} onMouseUp={onMouseUp}
            onMouseLeave={() => { setHoverId(null); if (drawing) { setDrawing(false); setLivePx(null); } }}
            onTouchStart={onMouseDown} onTouchMove={onMouseMove} onTouchEnd={onMouseUp}
            style={{ maxWidth: "100%", maxHeight: "calc(97vh - 185px)", width: "auto", height: "auto", display: status === "ready" ? "block" : "none", borderRadius: 8, boxShadow: "0 2px 12px rgba(0,0,0,0.1)", cursor: drawMode ? "crosshair" : "default", touchAction: "none" }}
          />
        </div>
        <div style={{ width: 320, flexShrink: 0, display: "flex", flexDirection: "column", background: "#fff", borderLeft: "1px solid #e2e8f0", overflow: "hidden" }}>
          <div style={{ padding: "10px 14px", borderBottom: "1px solid #f1f5f9", background: "#f8fafc", fontSize: 12, fontWeight: 700, color: "#475569", display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
            <span>Defects ({boxes.length})</span>
            <span style={{ fontSize: 10, color: "#94a3b8", fontWeight: 400 }}>existing · new</span>
          </div>
          {delCount > 0 && (<div style={{ padding: "6px 14px", background: "#fef2f2", borderBottom: "1px solid #fecaca", fontSize: 11, color: "#b91c1c", fontWeight: 600, flexShrink: 0 }}>{delCount} defect{delCount > 1 ? "s" : ""} pending removal</div>)}
          <div style={{ overflowY: "auto", flex: 1 }}>
            {boxes.length === 0 ? (
              <div style={{ padding: 28, textAlign: "center", color: "#94a3b8", fontSize: 12 }}>
                {status === "ready" ? 'No defects.\nEnable "Draw box" to add one.' : "Loading…"}
              </div>
            ) : (
              boxes.map((box) => {
                const orig = (allDefects || []).find((d) => d.defect_id === box.defect_id);
                const wasRenamed = box.isExisting && orig && orig.defect_name !== box.label;
                return (
                  <div key={box.id} style={{ padding: "10px 14px", borderBottom: "1px solid #f1f5f9", background: wasRenamed ? "#fefce8" : "#fff" }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <span style={{ fontSize: 10, color: "#94a3b8", fontWeight: 700, minWidth: 18 }}>#{box.defect_no ?? "?"}</span>
                        <div style={{ width: 10, height: 10, borderRadius: 2, background: box.color, flexShrink: 0 }} />
                        <span style={{ fontWeight: 700, color: "#1e293b", fontSize: 12 }}>{box.label}</span>
                        {wasRenamed && <Chip bg="#fef9c3" color="#ca8a04">renamed</Chip>}
                        <Chip bg={box.isExisting ? "#eff6ff" : "#f0fdf4"} color={box.isExisting ? "#2563eb" : "#16a34a"}>
                          {box.isExisting ? "existing" : "new"}
                        </Chip>
                      </div>
                      <div style={{ display: "flex", gap: 4 }}>
                        <SBtn bg="#eff6ff" border="#bfdbfe" color="#2563eb"
                          onClick={() => renamingId === box.id ? commitRename() : (setRenamingId(box.id), setRenameVal(box.label))}>
                          ✏ Rename
                        </SBtn>
                        <SBtn bg="#fef2f2" border="#fecaca" color="#ef4444" onClick={() => removeBox(box.id)}>Remove</SBtn>
                      </div>
                    </div>
                    {renamingId === box.id && (
                      <div style={{ marginTop: 6 }}>
                        <select autoFocus value={renameVal} onChange={(e) => setRenameVal(e.target.value)}
                          style={{ width: "100%", border: "1.5px solid #3b82f6", borderRadius: 6, padding: "4px 8px", fontSize: 11, outline: "none", background: "#fff", cursor: "pointer" }}
                        >
                          {Object.keys(DEFECT_COLORS).map((n) => (<option key={n} value={n}>{n}</option>))}
                        </select>
                        <div style={{ display: "flex", gap: 4, marginTop: 4 }}>
                          <SBtn bg="#f0fdf4" border="#bbf7d0" color="#16a34a" flex onClick={commitRename}>✓ Apply</SBtn>
                          <SBtn bg="#f8fafc" border="#e2e8f0" color="#64748b" flex onClick={() => setRenamingId(null)}>Cancel</SBtn>
                        </div>
                      </div>
                    )}
                    <div style={{ fontSize: 10, color: "#94a3b8", marginTop: 4 }}>
                      Side <b style={{ color: "#475569" }}>{box.defect.defect_side}</b>&nbsp;·&nbsp;
                      Img <b style={{ color: "#475569" }}>{box.defect.image_no}</b>&nbsp;·&nbsp;
                      ({box.x},{box.y})→({box.x + box.w},{box.y + box.h})
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ─── MediaViewer ──────────────────────────────────────────────────────────────
const MediaViewer = ({ images = [], video = [], viewType, imageType = "raw", scanStageId, existingDefects = [], onDefectsUpdated, rawImages = [], reportData = {}, roleName = "" }) => {
  const canEditDefects = roleName === DASHBOARD_EDITOR || roleName === SUPER_ADMIN;

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewSrc, setPreviewSrc] = useState("");
  const [previewName, setPreviewName] = useState("");
  const [previewFileName, setPreviewFileName] = useState("");
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorOriginal, setEditorOriginal] = useState("");
  const [editorFileName, setEditorFileName] = useState("");
  const [filterViewOpen, setFilterViewOpen] = useState(false);
  const [filterViewSrc, setFilterViewSrc] = useState("");
  const [filterViewFileName, setFilterViewFileName] = useState("");
  const [batchChanges, setBatchChanges] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState("");

  const totalChangedImages = Object.keys(batchChanges).length;
  const totalNew = Object.values(batchChanges).reduce((a, c) => a + c.boxes.filter((b) => !b.isExisting).length, 0);
  const totalDel = Object.values(batchChanges).reduce((a, c) => a + c.deletedIds.length, 0);
  const totalRenamed = Object.values(batchChanges).reduce((a, c) => a + c.boxes.filter((b) => b.isExisting && b.label !== (existingDefects || []).find((d) => d.defect_id === b.defect_id)?.defect_name).length, 0);
  const hasBatch = totalChangedImages > 0;

  const sortedImages = [...images].sort((a, b) => {
    const no = (fn) => {
      if (!fn) return 0;
      const clean = fn.replace(/_updated_[0-9]{8}(?:_[0-9]{6})?\./i, ".").replace(/_updated\./i, ".");
      const m = clean.match(/_(.+)_([0-9]+)(?:_(external|internal))?\.(\w+)$/i);
      return m ? parseInt(m[2], 10) : 0;
    };
    return no(a.file_name) - no(b.file_name);
  });

  const findOriginal = (fileName) => {
    const { image_no } = parseImageMeta(fileName);
    return rawImages.find((r) => parseImageMeta(r.file_name).image_no === image_no) || null;
  };

  const openEditor = (fileName) => {
    const orig = imageType !== "raw" ? findOriginal(fileName) : null;
    const src = orig ? orig.original : rawImages.find((r) => parseImageMeta(r.file_name).image_no === parseImageMeta(fileName).image_no)?.original || "";
    setEditorOriginal(src || "");
    setEditorFileName(orig ? orig.file_name : fileName);
    setPreviewOpen(false);
    setEditorOpen(true);
  };

  const openFilterView = (fileName, src) => {
    const orig = findOriginal(fileName);
    setFilterViewSrc(orig ? orig.original : src);
    setFilterViewFileName(orig ? orig.file_name : fileName);
    setPreviewOpen(false);
    setFilterViewOpen(true);
  };

  const handleCommit = (boxes, deletedIds, canvasEl) => {
    const key = editorFileName;
    const hasAnyChange = boxes.some((b) => !b.isExisting) || deletedIds.length > 0 ||
      boxes.some((b) => b.isExisting && b.label !== (existingDefects || []).find((d) => d.defect_id === b.defect_id)?.defect_name);
    setBatchChanges((prev) => {
      if (!hasAnyChange) { const n = { ...prev }; delete n[key]; return n; }
      return { ...prev, [key]: { boxes, deletedIds, canvasEl } };
    });
  };

  const handleSaveAll = async () => {
    if (!hasBatch) return;
    setSaving(true);
    setSaveMsg("");
    try {
      const allDefectPayload = [];
      Object.values(batchChanges).forEach(({ boxes, deletedIds }) => {
        boxes.forEach((b) => {
          if (b.isExisting) {
            const original = (existingDefects || []).find((d) => d.defect_id === b.defect_id);
            const wasRenamed = original && original.defect_name !== b.label;
            if (wasRenamed) allDefectPayload.push({
              _action: "update", defect_id: b.defect_id, defect_name: b.defect.defect_name,
              defect_side: b.defect.defect_side, image_no: b.defect.image_no,
              coordinates: b.defect.coordinates, defect_dimension: b.defect.defect_dimension,
              defect_code: b.defect.defect_code ?? null, is_significant_defect: original.is_significant_defect,
            });
          } else {
            allDefectPayload.push({
              _action: "create", defect_id: null, defect_name: b.defect.defect_name,
              defect_side: b.defect.defect_side, image_no: b.defect.image_no,
              coordinates: b.defect.coordinates, defect_dimension: b.defect.defect_dimension,
              defect_code: b.defect.defect_code ?? null, is_significant_defect: true,
            });
          }
        });
        deletedIds.forEach((id) => allDefectPayload.push({ _action: "delete", defect_id: id }));
      });

      const defectData = await updateScanStageDefect({ scan_stage_id: scanStageId, defects: allDefectPayload });

      await Promise.all(Object.entries(batchChanges).map(async ([fileName, { canvasEl }]) => {
        if (!canvasEl) return;
        const annotatedFileName = fileName.replace(/^original_/i, "annotated_");
        const ext = annotatedFileName.lastIndexOf(".");
        const updatedFileName = ext !== -1
          ? annotatedFileName.slice(0, ext) + "_updated" + annotatedFileName.slice(ext)
          : annotatedFileName + "_updated";
        const updatedFile = await canvasToFile(canvasEl, updatedFileName);
        const existingUpdated = (reportData?.scan_images || []).find((img) => img.file_name === updatedFileName);
        const form = new FormData();
        form.append("scan_stage_id", scanStageId);
        form.append("scan_id", reportData?.scan_id || "");
        form.append("company_id", reportData?.company_id || localStorage.getItem("company_id") || "");
        form.append("stage_name", reportData?.stage_name || "");
        if (existingUpdated?.file_name) form.append("replace_file_name", existingUpdated.file_name);
        form.append("file", updatedFile, updatedFileName);
        await updateScanStageImages(form);
      }));

      setSaveMsg("✓ All changes saved");
      setBatchChanges({});
      successMessage("All defect changes saved successfully.");
      if (onDefectsUpdated) onDefectsUpdated(defectData.defects);
    } catch (e) {
      console.error("Save all failed:", e);
      const errMsg = e?.response?.data?.message || e?.message || "Save failed";
      setSaveMsg("✗ " + errMsg);
      errorMessage(errMsg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <style>{`@keyframes shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }`}</style>
      {hasBatch && (
        <div style={{ margin: "0 0 12px 0", padding: "12px 16px", borderRadius: 10, background: "#fffbeb", border: "1.5px solid #fcd34d" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: "#92400e" }}>
              ⚡ Staged: {totalChangedImages} image{totalChangedImages > 1 ? "s" : ""}
              {totalNew > 0 && ` · +${totalNew} new`}
              {totalDel > 0 && ` · -${totalDel} removed`}
              {totalRenamed > 0 && ` · ${totalRenamed} renamed`}
            </span>
            <button onClick={handleSaveAll} disabled={saving}
              style={{ padding: "6px 18px", borderRadius: 8, border: "none", background: saving ? "#d1d5db" : "#16a34a", color: "#fff", fontSize: 13, fontWeight: 700, cursor: saving ? "not-allowed" : "pointer" }}>
              {saving ? "Saving…" : "💾 Save All Changes"}
            </button>
            <button onClick={() => { setBatchChanges({}); setSaveMsg(""); }} disabled={saving}
              style={{ padding: "6px 14px", borderRadius: 8, border: "1.5px solid #e2e8f0", background: "#fff", color: "#64748b", fontSize: 12, fontWeight: 600, cursor: saving ? "not-allowed" : "pointer" }}>
              Discard All
            </button>
            {saveMsg && <span style={{ fontSize: 12, fontWeight: 700, color: saveMsg.startsWith("✓") ? "#16a34a" : "#ef4444" }}>{saveMsg}</span>}
          </div>
        </div>
      )}

      <div className="flex gap-4 flex-wrap">
        {viewType === "images" ? (
          sortedImages.length > 0 ? (
            sortedImages.map((img, idx) => {
              const origFileName = rawImages.find((r) => parseImageMeta(r.file_name).image_no === parseImageMeta(img.file_name).image_no)?.file_name;
              const isPending = origFileName ? !!batchChanges[origFileName] : false;
              return (
                <div key={idx} style={{ width: 128 }}>
                  <div style={{ position: "relative", width: 128, height: 128 }}>
                    <div style={{ position: "absolute", inset: 0, borderRadius: 8, background: "linear-gradient(90deg, #e2e8f0 25%, #f8fafc 50%, #e2e8f0 75%)", backgroundSize: "200% 100%", animation: "shimmer 1.4s infinite" }} />
                    <img
                      src={img.thumb} alt={img.file_name} loading="lazy" decoding="async"
                      onLoad={(e) => { e.currentTarget.style.opacity = "1"; if (e.currentTarget.previousSibling) e.currentTarget.previousSibling.style.display = "none"; }}
                      onError={(e) => { if (e.currentTarget.previousSibling) e.currentTarget.previousSibling.style.background = "#fee2e2"; }}
                      onClick={() => { setPreviewSrc(img.original); setPreviewName(getImageLabel(img.file_name)); setPreviewFileName(img.file_name); setPreviewOpen(true); }}
                      style={{ position: "absolute", inset: 0, width: 128, height: 128, objectFit: "cover", borderRadius: 8, cursor: "pointer", display: "block", opacity: 0, transition: "opacity 0.25s ease", border: isPending ? "2.5px solid #f59e0b" : "none" }}
                    />
                    {isPending && (<div style={{ position: "absolute", top: 4, right: 4, zIndex: 1, background: "#f59e0b", color: "#fff", fontSize: 9, fontWeight: 700, padding: "2px 5px", borderRadius: 4 }}>staged</div>)}
                  </div>
                  <p style={{ fontSize: 11, textAlign: "center", marginTop: 4, color: "#475569", wordBreak: "break-word" }}>{getImageLabel(img.file_name)}</p>
                </div>
              );
            })
          ) : (<p style={{ color: "#94a3b8" }}>No images available.</p>)
        ) : video?.length > 0 ? (
          video.map((vid, idx) => (
            <div key={idx} style={{ width: 128 }}>
              <img src={Video} alt="Video" onClick={() => { setPreviewSrc(vid.file_url); setPreviewName("Video"); setPreviewOpen(true); }}
                style={{ width: 128, height: 128, objectFit: "cover", borderRadius: 8, cursor: "pointer" }} />
            </div>
          ))
        ) : (<p style={{ color: "#94a3b8" }}>No video available.</p>)}
      </div>

      {/* ── Preview Dialog ── */}
      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="lg" fullWidth PaperProps={{ sx: { maxHeight: "95vh", borderRadius: "12px" } }}>
        <DialogTitle sx={{ padding: "10px 16px", borderBottom: "1px solid #f1f5f9" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ fontSize: 14, fontWeight: 600, color: "#1e293b" }}>{previewName}</span>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              {imageType === "processed" && canEditDefects && (
                <button onClick={() => openEditor(previewFileName)}
                  style={{ display: "flex", alignItems: "center", gap: 6, padding: "5px 14px", borderRadius: 8, border: "1.5px solid #3b82f6", background: "#eff6ff", color: "#2563eb", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                  </svg>
                  Draw Defects
                </button>
              )}
              {imageType === "processed" && (
                <button onClick={() => openFilterView(previewFileName, previewSrc)}
                  style={{ display: "flex", alignItems: "center", gap: 6, padding: "5px 14px", borderRadius: 8, border: "1.5px solid #7c3aed", background: "#f5f3ff", color: "#7c3aed", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
                  🔍 Filter Defects
                </button>
              )}
              <IconButton onClick={() => setPreviewOpen(false)} size="small"><CloseIcon fontSize="small" /></IconButton>
            </div>
          </div>
        </DialogTitle>
        <DialogContent sx={{ padding: "12px", display: "flex", justifyContent: "center", overflow: "auto", background: "#f8fafc" }}>
          <img src={previewSrc} alt="Preview" style={{ maxWidth: "100%", maxHeight: "calc(95vh - 80px)", width: "auto", height: "auto", borderRadius: 6 }} />
        </DialogContent>
      </Dialog>

      {/* ── Canvas Editor ── */}
      <CanvasEditor open={editorOpen} onClose={() => setEditorOpen(false)} originalSrc={editorOriginal} fileName={editorFileName}
        allDefects={existingDefects} pendingBoxes={batchChanges[editorFileName]?.boxes || null}
        pendingDeletedIds={batchChanges[editorFileName]?.deletedIds || null} onCommit={handleCommit} />

      {/* ── Filter View ── */}
      <FilterView open={filterViewOpen} onClose={() => setFilterViewOpen(false)} imageSrc={filterViewSrc} fileName={filterViewFileName} allDefects={existingDefects} />
    </div>
  );
};

export default MediaViewer;